import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaHome, FaUser, FaUtensils, FaWeight, FaCalendarAlt, FaComments, FaSignOutAlt, FaBars, FaTimes, FaQuestionCircle, FaVideo } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import NotificationDropdown from '../components/notifications/NotificationDropdown';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  position: relative;
`;

const Sidebar = styled.aside`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  z-index: 999;
  transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease;
  width: 250px;
  padding: 2rem 0;
  
  @media (min-width: 992px) {
    position: fixed;
    transform: none;
    left: 0;
    top: 0;
  }
`;

const Content = styled.main`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
  overflow-y: auto;
  width: 100%;
  margin-left: 0;
  padding-top: 4rem;
  
  @media (min-width: 992px) {
    margin-left: 250px;
    width: calc(100% - 250px);
    padding-top: 1rem;
  }
`;

const ContentHeader = styled.div`
  display: none;
  
  @media (min-width: 992px) {
    display: block;
    padding-bottom: 1rem;
  }
`;

const ContentBody = styled.div`
  padding: 1rem;
`;

const Logo = styled.div`
  padding: 0 1.5rem;
  margin-bottom: 2rem;
  font-size: 1.25rem;
  font-weight: bold;
`;

const NavMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
`;

const NavItem = styled.li`
  margin-bottom: 0.5rem;
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.2s ease;
  background-color: ${({ $active }) => $active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const IconWrapper = styled.span`
  font-size: 1.2rem;
  display: flex;
  align-items: center;
`;

const SignOutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-size: 1rem;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const FooterSection = styled.div`
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
`;

const MobileNavbar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  z-index: 998;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 992px) {
    display: none;
  }
`;

const MobileLogoText = styled.div`
  font-size: 1.25rem;
  font-weight: bold;
`;

const MobileMenuToggle = styled.button`
  background: none;
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
`;

const MobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  
  @media (min-width: 992px) {
    display: none;
  }
`;

const MainContentWrapper = styled.div`
  width: 100%;
`;

const CustomerLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Close the sidebar when changing routes on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);
  
  const isPathActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  
  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };
  
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };
  
  return (
    <LayoutContainer>
      <MobileNavbar>
        <MobileMenuToggle onClick={toggleSidebar} aria-label="Toggle menu">
          {isSidebarOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuToggle>
        <MobileLogoText>Geo Diet Wonder</MobileLogoText>
        <MobileActions>
          <NotificationDropdown />
        </MobileActions>
      </MobileNavbar>
      
      <Overlay $isOpen={isSidebarOpen} onClick={closeSidebar} />
      
      <Sidebar $isOpen={isSidebarOpen}>
        <Logo>Geo Diet Wonder</Logo>
        <NavMenu>
          <NavItem>
            <NavLink to="/customer" $active={location.pathname === '/customer'} onClick={closeSidebar}>
              <IconWrapper><FaHome /></IconWrapper>
              Dashboard
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/customer/meal-plans" $active={isPathActive('/customer/meal-plans')} onClick={closeSidebar}>
              <IconWrapper><FaUtensils /></IconWrapper>
              Daily Plans
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/customer/progress" $active={isPathActive('/customer/progress')} onClick={closeSidebar}>
              <IconWrapper><FaWeight /></IconWrapper>
              My Progress
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/customer/daily" $active={isPathActive('/customer/daily')} onClick={closeSidebar}>
              <IconWrapper><FaWeight /></IconWrapper>
              Update Daily Progress
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/customer/videos" $active={isPathActive('/customer/videos')} onClick={closeSidebar}>
              <IconWrapper><FaVideo /></IconWrapper>
              Videos
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/customer/consultations" $active={isPathActive('/customer/consultations')} onClick={closeSidebar}>
              <IconWrapper><FaCalendarAlt /></IconWrapper>
              Consultations
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/customer/chat" $active={isPathActive('/customer/chat')} onClick={closeSidebar}>
              <IconWrapper><FaComments /></IconWrapper>
              Chat with Partner
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/customer/support" $active={isPathActive('/customer/support')} onClick={closeSidebar}>
              <IconWrapper><FaQuestionCircle /></IconWrapper>
              Support
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/customer/profile" $active={isPathActive('/customer/profile')} onClick={closeSidebar}>
              <IconWrapper><FaUser /></IconWrapper>
              Profile
            </NavLink>
          </NavItem>
        </NavMenu>
        
        <FooterSection>
          <SignOutButton onClick={handleSignOut}>
            <IconWrapper><FaSignOutAlt /></IconWrapper>
            Sign Out
          </SignOutButton>
        </FooterSection>
      </Sidebar>
      
      <MainContentWrapper>
        <Content>
          <ContentHeader>
            <Header />
          </ContentHeader>
          <ContentBody>
            {children}
          </ContentBody>
        </Content>
      </MainContentWrapper>
    </LayoutContainer>
  );
};

export default CustomerLayout; 